import React, {Component} from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {Button} from '../button/button';

import styles from './editable-label.module.css';

type SavingStatus = 'SAVING' | 'SUCCESS' | 'FAILURE';

export type EditableLabelVariant = 'default' | 'header';

type Props = {
    value: string;
    editValue?: string;
    onSaveNewValue: (arg1: string) => Promise<any>;
    placeholder: string;
    allowSavingEmpty: boolean;
    shouldAllowUndo?: boolean;
    required?: boolean;
    tooltip?: string | null | undefined;
    customStyle?: any;
    isWhite?: boolean;
    shouldTruncate?: boolean;
    shouldCenterText?: boolean;
    variant?: EditableLabelVariant;
};

type State = {
    isEditing: boolean;
    editedValue: string;
    savingStatus?: SavingStatus;
    lastValue?: string;
};

export class EditableLabel extends Component<Props, State> {
    input: HTMLInputElement | null | undefined;
    activeTimeout: number | null | undefined;

    static defaultProps = {
        placeholder: 'Add name',
        allowSavingEmpty: false,
        shouldAllowUndo: true,
        required: false,
        tooltip: 'Rename',
        shouldTruncate: false,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            isEditing: false,
            editedValue: props.editValue ?? (props.value || ''),
            lastValue: undefined,
        };
    }

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.value !== this.props.value) {
            this.setState({
                isEditing: false,
            });
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevState.isEditing !== this.state.isEditing && this.state.isEditing && this.input) {
            if (this.activeTimeout) {
                clearTimeout(this.activeTimeout);
            }
            this.input.select();
        }
        if (
            (prevState.savingStatus !== this.state.savingStatus &&
                this.state.savingStatus === 'SUCCESS') ||
            this.state.savingStatus === 'FAILURE'
        ) {
            // @ts-expect-error
            this.activeTimeout = setTimeout(() => {
                this.setState({savingStatus: undefined, lastValue: undefined});
            }, 6000);
        }
    }

    render() {
        const labelStyleName = classnames(styles['editable-label'], {
            [styles['editable-label--header']]: this.props.variant === 'header',
            [styles['editable-label--white']]: this.props.isWhite,
            [styles['editable-label--required']]: this.props.required,
            [styles['editable-label--failure']]: this.state.savingStatus === 'FAILURE',
            [styles['editable-label--saving']]: this.state.savingStatus === 'SAVING',
            [styles['editable-label--empty']]: !this.props.value,
        });

        const containerClassName = classnames(styles['editable-label-container'], {
            [styles['editable-label-container--centered']]: this.props.shouldCenterText,
        });

        return this.state.isEditing ? (
            <div className={containerClassName}>
                <input
                    ref={(node?: HTMLInputElement | null) => {
                        this.input = node;
                    }}
                    value={this.state.editedValue}
                    onKeyDown={this.handleKeydown}
                    placeholder={this.props.placeholder}
                    onChange={this.handleChange}
                    onBlur={this.handleSubmit}
                    className={classnames(
                        this.props.isWhite
                            ? styles['editable-label-input--white']
                            : styles['editable-label-input'],
                        {
                            [styles['editable-label-input--header']]:
                                this.props.variant === 'header' && !this.props.isWhite,
                        }
                    )}
                />
            </div>
        ) : (
            <div
                className={classnames(containerClassName, {
                    [styles['editable-label-container--truncate']]: this.props.shouldTruncate,
                })}
            >
                <button
                    data-tip={this.props.tooltip ? this.props.tooltip : undefined}
                    onClick={this.toggleEdit}
                    style={this.props.customStyle}
                    className={labelStyleName}
                >
                    {this.props.value || this.props.placeholder}
                </button>
                {this.props.shouldAllowUndo &&
                this.state.lastValue &&
                this.state.savingStatus === 'SUCCESS' ? (
                    <Button
                        variant='text-primary'
                        size='big'
                        onClick={() => {
                            if (this.state.lastValue) {
                                this.saveNewValue(this.state.lastValue);
                                // Remove undo button once it's been clicked
                                this.setState({lastValue: undefined});
                            }
                        }}
                    >
                        Undo
                    </Button>
                ) : null}
            </div>
        );
    }

    handleChange = (e: React.ChangeEvent<any>) => {
        this.setState({editedValue: e.target.value});
    };

    handleSubmit = () => {
        const originalEditValue = this.props.editValue ?? this.props.value;
        if (
            (!this.props.allowSavingEmpty && !this.state.editedValue.length) ||
            originalEditValue === this.state.editedValue
        ) {
            this.setState({isEditing: false});
        } else {
            this.saveNewValue(this.state.editedValue);
        }
    };

    saveNewValue = (newValue: string) => {
        this.setState({savingStatus: 'SAVING', isEditing: false});
        this.props
            .onSaveNewValue(newValue)
            .then(() => {
                this.setSavingStatusWithTimeout('SUCCESS');
            })
            .catch(() => {
                this.setSavingStatusWithTimeout('FAILURE');
            });
    };

    setSavingStatusWithTimeout = (status: SavingStatus) => {
        if (this.activeTimeout) {
            clearTimeout(this.activeTimeout);
        }
        this.setState({savingStatus: status});
    };

    toggleEdit = () => {
        const originalEditValue = this.props.editValue ?? this.props.value;
        this.setState((prevState: State) => {
            return {
                editedValue: originalEditValue,
                isEditing: !prevState.isEditing,
                lastValue: prevState.isEditing ? undefined : this.props.value,
                // If we started editing, clear any saving status since we're going to cancel the timeout
                savingStatus: !prevState.isEditing ? undefined : prevState.savingStatus,
            };
        });
    };

    handleKeydown = (e: React.KeyboardEvent<any>) => {
        if (e.key === 'Escape') {
            this.toggleEdit();
        }
        if (e.key === 'Enter') {
            this.handleSubmit();
        }
    };
}
